import { EditableRowList } from "./EditableRowList";
import { commitHandlers, fieldInvalid, rowErrorMessages } from "./row-fields";
import { RowSaveFooter, RowStatusIcon } from "./RowSaveState";
import { fieldClass } from "./fields";
import type { CatalogEditor, EditorRow } from "./useCatalogEditor";

type CycleRow = EditorRow<"cycles">;

export type CyclesEditorProps = {
  editor: CatalogEditor;
  onRemove: (row: CycleRow) => void;
  /** Under the rows — the note on how programs by cycle relate to these. */
  footer?: React.ReactNode;
  /** Replaces the title row — it carries the cycles on/off switch. */
  header?: React.ReactNode;
  disabled?: boolean;
  /** Desktop: order by dragging instead of the arrows. */
  sortable?: boolean;
};

export function CyclesEditor({
  editor,
  onRemove,
  footer,
  header,
  disabled,
  sortable,
}: CyclesEditorProps): React.JSX.Element {
  return (
    <EditableRowList<CycleRow>
      title="Ciclos escolares"
      hint={
        <>
          La clave es un identificador interno; la etiqueta es lo que dice el
          agente. No se derivan una de la otra —{" "}
          <code className="rounded bg-ink-050 px-1 py-0.5 font-mono text-[11.5px] text-ink-700">
            2026-2
          </code>{" "}
          puede llamarse «Enero – Junio 2027» si el ciclo 2 arranca en enero.
        </>
      }
      items={editor.rows.cycles}
      getKey={(row) => row.key}
      onAdd={() => editor.add("cycles", { key: "", label: "" })}
      onRemove={onRemove}
      onReorder={(rows) => editor.reorder("cycles", rows.map((r) => r.key))}
      canMove={(row) => row.id !== null}
      addLabel="Agregar ciclo"
      emptyLabel="Aún no hay ciclos."
      rowName={(row, i) =>
        row.current.key.trim() ? `el ciclo ${row.current.key}` : `el ciclo ${i + 1}`
      }
      rowErrors={(row) => rowErrorMessages(editor, "cycles", row.key)}
      renderRowStatus={(row) => <RowStatusIcon status={row.status} />}
      renderRowFooter={(row) => <RowSaveFooter editor={editor} list="cycles" row={row} />}
      disabled={disabled}
      footer={footer}
      header={header}
      sortable={sortable}
      renderRow={(row, index) => (
        <>
          <input
            className={`${fieldClass({
              mono: true,
              invalid: fieldInvalid(editor, "cycles", row.key, "key"),
            })} w-[108px] shrink-0`}
            value={row.current.key}
            disabled={disabled}
            placeholder="2026-1"
            aria-label={`Clave del ciclo ${index + 1}`}
            onChange={(e) => editor.edit("cycles", row.key, { key: e.target.value }, "debounce")}
            {...commitHandlers(editor, "cycles", row.key)}
          />
          <input
            className={`${fieldClass({
              invalid: fieldInvalid(editor, "cycles", row.key, "label"),
            })} min-w-[160px] flex-1`}
            value={row.current.label}
            disabled={disabled}
            placeholder="Enero – Junio 2026"
            aria-label={`Etiqueta del ciclo ${index + 1}`}
            onChange={(e) =>
              editor.edit("cycles", row.key, { label: e.target.value }, "debounce")
            }
            {...commitHandlers(editor, "cycles", row.key)}
          />
        </>
      )}
    />
  );
}
